import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminMemberAccessList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const ACCESS_PAGE_SIZE = 30;
const accessSortOptions = [
  { value: 'updated-desc', label: '更新日が新しい順' },
  { value: 'active-status', label: '有効状態順' },
  { value: 'email-asc', label: 'メールアドレス昇順' },
  { value: 'email-desc', label: 'メールアドレス降順' },
];

const accessStatusToUi = (isActive) => ({
  label: isActive ? '有効' : '停止中',
  tone: isActive ? 'published' : 'draft',
});

const tableStyle = {
  minWidth: '68rem',
};

const indexColumnStyle = {
  width: '4.5rem',
  minWidth: '4.5rem',
};

const accountColumnStyle = {
  minWidth: '18rem',
};

const qualificationColumnStyle = {
  width: '8rem',
  minWidth: '8rem',
};

const statusColumnStyle = {
  width: '9rem',
  minWidth: '9rem',
};

const dateColumnStyle = {
  width: '11rem',
  minWidth: '11rem',
};

const actionColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
};

const sortAccessRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'active-status') {
      if (left.isActive === right.isActive) {
        return left.accountEmail.localeCompare(right.accountEmail, 'ja');
      }
      return left.isActive ? -1 : 1;
    }

    if (sortKey === 'email-asc') {
      return left.accountEmail.localeCompare(right.accountEmail, 'ja');
    }

    if (sortKey === 'email-desc') {
      return right.accountEmail.localeCompare(left.accountEmail, 'ja');
    }

    const leftDate = Date.parse(left.updatedAt.replace(' ', 'T'));
    const rightDate = Date.parse(right.updatedAt.replace(' ', 'T'));
    const safeLeft = Number.isNaN(leftDate) ? 0 : leftDate;
    const safeRight = Number.isNaN(rightDate) ? 0 : rightDate;
    return safeRight - safeLeft;
  });

  return nextRows;
};

const AdminMemberAccessListPage = () => {
  const content = yowanPageContent['/admin/access/member'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [sortKey, setSortKey] = useState('updated-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadAccessRows = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminMemberAccessList({
          page: currentPage,
          pageSize: ACCESS_PAGE_SIZE,
          q: searchQuery.trim(),
          isActive: isActiveFilter,
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError('会員資格を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAccessRows();

    return () => {
      isMounted = false;
    };
  }, [content, currentPage, isActiveFilter, searchQuery]);

  if (!content) {
    return null;
  }

  const sortedRows = sortAccessRows(rows, sortKey);
  const totalPages = Math.max(1, Math.ceil(apiCount / ACCESS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * ACCESS_PAGE_SIZE;
  const visibleRows = sortedRows;

  return (
    <AdminBlogShell activeNavHref="/admin/access/member" routeKey="/admin/access/member">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${apiCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="氏名 / ふりがな / メールアドレス"
        sortOptions={accessSortOptions}
      >
        <div className="yowan-admin-toolbar__group">
          <label className="c-form__field yowan-admin-toolbar__field">
            <span className="c-form__label">状態</span>
            <select
              className="c-form__control yowan-admin-toolbar__sort"
              value={isActiveFilter}
              onChange={(event) => {
                setIsActiveFilter(event.target.value);
                setCurrentPage(1);
              }}
            >
              {content.filters.isActive.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </AdminListToolbar>
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table" style={tableStyle}>
          <thead>
            <tr>
              <th scope="col" style={indexColumnStyle}>#</th>
              <th scope="col" style={accountColumnStyle}>氏名 / メールアドレス</th>
              <th scope="col" style={qualificationColumnStyle}>資格</th>
              <th scope="col" style={statusColumnStyle}>状態</th>
              <th scope="col" style={dateColumnStyle}>作成日</th>
              <th scope="col" style={dateColumnStyle}>更新日</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const statusUi = accessStatusToUi(row.isActive);

              return (
                <tr key={row.id}>
                  <td style={indexColumnStyle}>{startIndex + index + 1}</td>
                  <td style={accountColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.accountName}</p>
                    <p className="yowan-admin-resource-table__subline">{row.accountEmail}</p>
                  </td>
                  <td style={qualificationColumnStyle}>{row.qualificationLabel}</td>
                  <td style={statusColumnStyle}>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}>
                      {statusUi.label}
                    </span>
                  </td>
                  <td style={dateColumnStyle}>{row.createdAt || '-'}</td>
                  <td style={dateColumnStyle}>{row.updatedAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && visibleRows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  {loadError || '表示できる会員資格がありません。'}
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={7}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminMemberAccessListPage;
